import { Navigate, Route, Routes } from 'react-router-dom';
import { Header } from './components/Header';
import { Footer } from './components/Footer';
import { RouteFocus } from './a11y/RouteFocus';
import { useAuth } from './auth/AuthContext';
import { Home } from './pages/Home';
import { DealDetail } from './pages/DealDetail';
import { SignIn } from './pages/SignIn';
import { Checkout } from './pages/Checkout';
import { OrderResult } from './pages/OrderResult';
import { Account } from './pages/Account';
import { MyVouchers } from './pages/MyVouchers';
import { VoucherDetail } from './pages/VoucherDetail';
import { VoucherPass } from './pages/VoucherPass';
import { VoucherPrint } from './pages/VoucherPrint';
import { PayAtCounter } from './pages/PayAtCounter';
import { ReceiptDetail } from './pages/ReceiptDetail';
import { SupportTickets } from './pages/SupportTickets';
import { PlaceScan } from './pages/PlaceScan';
import { Unsubscribe } from './pages/Unsubscribe';
import { HowItWorks, Faq, Support, Terms, Privacy } from './pages/StaticPages';
import { SmsOptIn } from './pages/SmsOptIn';
import { WelcomeAd } from './components/WelcomeAd';
import type { ReactElement } from 'react';

export function App() {
  return (
    <>
      {/* First tab stop on every page. Hidden until focused — see .skip-link.
          Without it a keyboard user tabs through the entire header before
          reaching the content, on every single navigation. */}
      <a href="#main" className="skip-link">Skip to content</a>

      <Header />

      <main className="page" id="main">
        <RouteFocus>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/deals/:dealId" element={<DealDetail />} />
            <Route path="/signin" element={<SignIn />} />

            {/* ANONYMOUS, and it must stay that way: the person holding the
                link may not have an account. Outside <Protected> on purpose. */}
            <Route path="/pass/:token" element={<VoucherPass />} />
            <Route path="/at/:token" element={<PlaceScan />} />

            {/* Written pages. Public and unauthenticated on purpose: a shopper
                deciding whether to hand over money must be able to read the terms
                without first creating an account, and app-store reviewers open
                these cold. */}
            <Route path="/how-it-works" element={<HowItWorks />} />
            <Route path="/faq" element={<Faq />} />
            <Route path="/support" element={<Support />} />

            {/* From the link in a promotional email. Anonymous by law (CAN-SPAM). */}
            <Route path="/unsubscribe" element={<Unsubscribe />} />

            {/* Emails sent before the one-click link existed pointed here, and the
                route did not exist - every "Unsubscribe" landed on the homepage.
                Kept so those emails now reach the settings that do the job. */}
            <Route path="/account/notifications" element={<Navigate to="/account" replace />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/sms" element={<SmsOptIn />} />

            <Route path="/checkout/:dealId" element={<Protected><Checkout /></Protected>} />
            <Route path="/orders/:orderId" element={<Protected><OrderResult /></Protected>} />
            <Route path="/account" element={<Protected><Account /></Protected>} />
            <Route path="/vouchers" element={<Protected><MyVouchers /></Protected>} />
            <Route path="/vouchers/:voucherId" element={<Protected><VoucherDetail /></Protected>} />
            <Route path="/vouchers/:voucherId/print" element={<Protected><VoucherPrint /></Protected>} />
            <Route path="/pay/:paymentId" element={<Protected><PayAtCounter /></Protected>} />
            <Route path="/receipts/:redemptionId" element={<Protected><ReceiptDetail /></Protected>} />
            <Route path="/help" element={<Protected><SupportTickets /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </RouteFocus>
      </main>

      <Footer />

      {/* The super admin's welcome ad: decides for itself whether to show. */}
      <WelcomeAd />
    </>
  );
}

/**
 * Waits for the stored session to be checked before deciding.
 *
 * Without the `ready` gate, a reload bounces a signed-in customer to the sign-in
 * page for a moment and loses where they were going — most visibly in the middle of
 * a checkout, which is the worst possible place to interrupt someone.
 */
function Protected({ children }: { children: ReactElement }) {
  const { userId, ready } = useAuth();

  if (!ready) {
    // role="status" so the wait is announced rather than being silence.
    return (
      <p className="muted" style={{ padding: '48px 0' }} role="status">
        Loading…
      </p>
    );
  }

  if (userId === null) {
    // `replace`, so the back button does not return to a page they cannot see.
    return <Navigate to={`/signin?next=${encodeURIComponent(window.location.pathname)}`} replace />;
  }

  return children;
}
